/**
 * CreateNominationPage — a BD nominates a guest to one event.
 *
 * Route: `/event/nominations/new`, optionally `?eventId=…`.
 *
 * Flat rather than nested under the event, mirroring the service: `POST /nominations`
 * carries `eventId` in the body and there is no `/events/{id}/nominations` resource, so a
 * nested frontend route would imply a hierarchy the API does not have.
 *
 * The event can arrive two ways, and the difference is deliberate:
 *
 *   ?eventId=…   from a New Events card or an event page. The picker shows that event and
 *                is LOCKED — the caller already decided, and letting it be changed here
 *                would make it possible to nominate to the wrong event by accident.
 *   no eventId   from the calendar's Nominate button. The picker is open, listing only
 *                events actually open for nomination (the same predicate the cards use),
 *                because those are the only ones the service would accept.
 *
 * Reached only by someone who can nominate — BD or admin per the service's route registry
 * for POST /nominations, which excludes marketers.
 *
 * ── What the service cannot store yet ──────────────────────────────────────────────────
 *
 * The form follows the Figma, but `CreateNomination` accepts only companyName / sfdcAccountId /
 * accountTags and the guest's firstName / lastName / jobTitle / phoneNumber / email /
 * businessUnit plus the picked contact's Customer Link objectId / objectType (and the BD's own
 * phone and BU, below). One designed contact field, Company Revenue Size, has nowhere to go.
 *
 * It is rendered as an ordinary field regardless, so the form matches the design. It used to be
 * flagged in place — an amber rule and a "Not saved yet" hint — which was accurate but made the
 * form look broken, so the flagging is gone and this note is the record. It is rendered through
 * UnsavedField below; that component is the list.
 *
 * ── The BD's own phone and BU ──────────────────────────────────────────────────────────
 *
 * The phone is the BD's to supply (`bdCellPhone`, required): validated as the service validates
 * it (utils/bdProfile), and pre-filled from `GET /me` with what this BD last submitted (the
 * acted-as BD's, under impersonation) unless the BD has already typed into it.
 *
 * The BU is not. Product decision: a BD's BU is taken from the GCR sales hierarchy. The service
 * resolves it, returns it on `GET /me` as `bdBusinessUnit`, and stamps it on the nomination itself
 * — CreateNomination ignores any BU sent — so it is shown read-only and not submitted. A BD whose
 * territories span several BUs gets the first, for simplicity, and the field says so. When it is
 * absent (none resolved, or the lookup failed) the BD cannot nominate, and the form says so rather
 * than letting them submit into a 400. The BU the BD picked on an earlier nomination is not used.
 *
 * ── Account tags ───────────────────────────────────────────────────────────────────────
 *
 * `accountInformation.accountTags` is a multi-select over ABM / T2K / DEEP / GREENFIELD / OTHERS, pre-filled
 * from the picked contact's SFDC account tags and editable by the BD (utils/accountTags). There
 * used to be an Account Type field; the service dropped `accountType` (Customer Link has no such
 * concept), so the field is gone.
 *
 * ── Travel & Event Arrangements ────────────────────────────────────────────────────────
 *
 * Not a fixed section: the questions are whatever the marketer configured on THIS event
 * (`Event.nominationForm`), so the section is empty for an event that asks nothing extra.
 *
 * Answers go back as `formAnswers`, matched to the questions BY POSITION — which is why the
 * answers are held in an array indexed the same as `nominationForm` rather than keyed by the
 * question text, and why every question is sent even when unanswered (as ""). A sparse or
 * reordered array would attach answers to the wrong questions. It is also why the service
 * locks the form once an event is published.
 *
 * ── Edit mode ──────────────────────────────────────────────────────────────────────────
 *
 * The same form edits a submitted nomination (`NominationForm editing={…}`, mounted by
 * EditNominationPage at `/event/nominations/:nominationId/edit`) — UpdateNomination, whose rules
 * are utils/nominationEdit's. What differs from create:
 *
 *   - the event is the nomination's and locked; there is no picker;
 *   - every field starts from the nomination, and the answers from its `formResponses`;
 *   - the BD BU shown is the one stamped on the nomination: UpdateNomination does not touch it,
 *     and does not apply create's BU rule — so neither /me's BU nor its absence gates an edit;
 *   - only what changed is sent (buildNominationPatch), and Save is disabled until something has;
 *   - saving an APPROVED / REJECTED nomination, or one whose attendance was confirmed, is
 *     confirmed first: the edit sends it back to review and clears the decision and attendance.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autosuggest,
  Multiselect,
  Box,
  BreadcrumbGroup,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  Select,
  SpaceBetween,
  type SelectProps,
} from '@amzn/awsui-components-react';
import {
  eventHubCalendarClient,
  type Contact,
  type Event,
  type CreateNominationInput,
  type Nomination,
} from '../../client/EventCalendarClient';
import { useEventList } from '../../hooks/useEventList';
import { useEventHubRoles } from '../../hooks/useEventHubRoles';
import { eventIsForBusinessUnits, nominatableEvents } from '../../utils/nominatableEvents';
import {
  applyAutofill,
  contactAccountTags,
  contactValues,
  formatRevenueText,
  type AutofillValues,
} from '../../utils/contactAutofill';
import { canNominate } from '../../utils/authorization';
import './CreateNominationPage.css';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import { BUSINESS_UNITS } from '../../businessUnits';
import { isValidBdPhone } from '../../utils/bdProfile';
import { applyTagFill, canonicalTags, tagOptions } from '../../utils/accountTags';
import { JOB_TITLE_OPTIONS, jobTitleLabel } from '../../utils/jobTitles';
import {
  contactNameOf,
  contactNameParts,
  contactRef,
  guestNameOf,
} from '../../utils/guestName';
import { loadContactPages } from '../../utils/paging';
import { useEventHubLogin } from '../../hooks/useEventHubLogin';
import {
  answersFromResponses,
  buildNominationPatch,
  editBlock,
  editConsequences,
  editErrorMessage,
  type NominationEdits,
} from '../../utils/nominationEdit';
import { nominationHref } from '../../utils/nominationLink';

interface Draft {
  eventId: string;
  /**
   * One answer per question on the chosen event's form, in the same order. Indexed, not
   * keyed by question text: `formAnswers` is positional, and two identically-worded
   * questions would collapse into one key.
   */
  formAnswers: string[];
  /** What is typed in the contact box — a picked contact's name, or a free-text one. */
  contactSearch: string;
  sfdcAccount: string;
  companyName: string;
  companyRevenueSize: string;
  /** The guest's given and family names — the service stores them apart (see guestName). */
  firstName: string;
  lastName: string;
  /**
   * The Customer Link record of the picked contact, sent as GuestInformation.objectId /
   * objectType so the service can match this nomination to the campaign member it becomes.
   * Empty for a guest typed in by hand.
   */
  contactRef: { objectId?: string; objectType?: string };
  jobTitle: string;
  phoneNumber: string;
  email: string;
  /** The selected account tags, submitted as accountInformation.accountTags. */
  accountTags: string[];
  /** What the last contact pick put into accountTags — see applyTagFill. Never sent. */
  tagsFilled: string[];
  /** The BD's own cell phone, as typed. Required. */
  bdCellPhone: string;
  /**
   * The customer's (guest's) BU, sent as guestInformation.businessUnit. Free text with the BU list
   * as suggestions: the service stores it as given, and the contact's SFDC account BU fills it.
   */
  customerBu: string;
  /**
   * What the last contact pick wrote into the fields below — how a field the BD has since
   * edited is told apart from one they left alone (see contactAutofill). Never sent.
   */
  autofilled: AutofillValues;
}

/** The account tags after a contact (re)pick: the contact's, unless the BD changed them. */
function tagFill(d: Draft, match: Contact | undefined): Pick<Draft, 'accountTags' | 'tagsFilled'> {
  const { tags, filled } = applyTagFill(
    d.accountTags,
    d.tagsFilled,
    canonicalTags(contactAccountTags(match)),
  );
  return { accountTags: tags, tagsFilled: filled };
}

/** A picked contact's names as the form holds them: always strings, '' for a missing part. */
function namesOf(c: Contact): Pick<Draft, 'firstName' | 'lastName'> {
  const { firstName, lastName } = contactNameParts(c);
  return { firstName: firstName ?? '', lastName: lastName ?? '' };
}

/** A contact option's tags: its SFDC account tags, then the account's revenue when known. */
function contactOptionTags(c: Contact): string[] | undefined {
  const tags = [...(c.accountTags ?? [])];
  const revenue = formatRevenueText(c.sfdcAccountAnnualRevenue);
  if (revenue) tags.push(`Revenue ${revenue}`);
  return tags.length ? tags : undefined;
}

/* Suggestions for the customer's BU only — the BD's own BU is never chosen from this list. */
const BU_OPTIONS = BUSINESS_UNITS.map((bu) => ({ value: bu, label: bu }));

/**
 * The GCR sales hierarchy (Brainforce), where a BD's territories and BU are managed. The form
 * points at it under the contact box and the BD BU, so a BD who sees the wrong contacts or BU
 * knows where that comes from and where to look.
 */
const SALES_HIERARCHY_URL = 'https://brainforce.dci.aws.a2z.org.cn/sales-hierarchy';

function SalesHierarchyLink() {
  return (
    <Link href={SALES_HIERARCHY_URL} external variant="primary" fontSize="body-s">
      sales hierarchy
    </Link>
  );
}

const EMPTY: Draft = {
  eventId: '',
  formAnswers: [],
  contactSearch: '',
  sfdcAccount: '',
  companyName: '',
  companyRevenueSize: '',
  firstName: '',
  lastName: '',
  contactRef: {},
  jobTitle: '',
  phoneNumber: '',
  email: '',
  bdCellPhone: '',
  customerBu: '',
  accountTags: [],
  tagsFilled: [],
  autofilled: {},
};

/**
 * The form, filled from a submitted nomination — the edit mode's starting point.
 *
 * The prefilled contact fields count as autofilled: they came from whoever the BD picked when
 * submitting, so picking a different contact now replaces them all rather than leaving the old
 * guest's details beside the new guest's name (contactAutofill's rule, applied to the stored
 * values). Answers are positional, one per stored response; the form is locked once published,
 * so those are the event's questions in order.
 */
function draftFrom(n: Nomination): Draft {
  const a = n.accountInformation;
  const g = n.guestInformation;
  const filled: AutofillValues = {
    sfdcAccount: a?.sfdcAccountId ?? '',
    companyName: a?.companyName ?? '',
    jobTitle: g?.jobTitle ?? '',
    phoneNumber: g?.phoneNumber ?? '',
    email: g?.email ?? '',
    customerBu: g?.businessUnit ?? '',
  };
  /* As stored, not canonicalised: rewriting them would read as an edit the BD did not make. */
  const tags = [...(a?.accountTags ?? [])];
  return {
    ...EMPTY,
    eventId: n.eventId ?? '',
    formAnswers: answersFromResponses(n.formResponses?.length ?? 0, n.formResponses),
    contactSearch: guestNameOf(g) ?? '',
    sfdcAccount: filled.sfdcAccount!,
    companyName: filled.companyName!,
    firstName: g?.firstName ?? '',
    lastName: g?.lastName ?? '',
    contactRef: g?.objectId || g?.objectType ? { objectId: g.objectId, objectType: g.objectType } : {},
    jobTitle: filled.jobTitle!,
    phoneNumber: filled.phoneNumber!,
    email: filled.email!,
    bdCellPhone: n.bdCellPhone ?? '',
    customerBu: filled.customerBu!,
    accountTags: tags,
    tagsFilled: tags,
    autofilled: filled,
  };
}

/**
 * What the form submits, shaped for the wire — one builder for create and edit, so the two cannot
 * drift on how a field is trimmed or when it is omitted.
 */
function formValues(draft: Draft, questionCount: number): NominationEdits {
  return {
    accountInformation: {
      companyName: draft.companyName.trim() || undefined,
      sfdcAccountId: draft.sfdcAccount.trim() || undefined,
      accountTags: draft.accountTags.length ? draft.accountTags : undefined,
    },
    bdCellPhone: draft.bdCellPhone.trim(),
    guestInformation: {
      firstName: draft.firstName.trim() || undefined,
      lastName: draft.lastName.trim() || undefined,
      jobTitle: draft.jobTitle.trim() || undefined,
      phoneNumber: draft.phoneNumber.trim() || undefined,
      email: draft.email.trim() || undefined,
      businessUnit: draft.customerBu.trim() || undefined,
      ...draft.contactRef,
    },
    /* One entry per question, in question order, with "" for the ones left blank — every
       question is optional, but a short array would shift later answers up onto the wrong
       questions. Omitted entirely when the event asks nothing. */
    formAnswers: questionCount
      ? Array.from({ length: questionCount }, (_, i) => draft.formAnswers[i] ?? '')
      : undefined,
  };
}

/**
 * One of the designed fields the service has no home for yet.
 *
 * Identical to the fields around it on purpose: it was marked in place before (an amber left
 * rule and a "Not saved yet" description) and that read as a fault in the form rather than as
 * information. The fact is recorded at the top of this file instead.
 *
 * Still its own component rather than a plain FormField so the set stays findable in the code:
 * every call site is a field the service drops, and turning those calls into ordinary
 * FormFields is the whole of the cleanup once the model accepts them.
 */
function UnsavedField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <FormField label={label}>
      <Input value={value} onChange={({ detail }) => onChange(detail.value)} />
    </FormField>
  );
}

/**
 * One form per ?eventId=. The route stays mounted when only the query changes (a chat
 * link to another event's form opens in this tab), and the draft would keep the guest
 * typed for the first event, to be submitted to the second. Keying by the event starts
 * a blank form instead.
 */
export default function CreateNominationPage() {
  const [params] = useSearchParams();
  return <NominationForm key={params.get('eventId') ?? ''} />;
}

/**
 * The nomination form. Creates by default; edits `editing` when given (see "Edit mode" above).
 * Keyed by its caller — per ?eventId= for create, per nomination for edit — so a draft never
 * carries over to a different event or nomination.
 */
export function NominationForm({ editing }: { editing?: Nomination } = {}) {
  const [params] = useSearchParams();
  const lockedEventId = editing ? (editing.eventId ?? '') : params.get('eventId');
  const navigate = useNavigate();
  const mayNominate = canNominate(useEventHubRoles());
  const { login } = useEventHubLogin();

  /* Skipped outright when the caller cannot nominate: the early return below decides what
     renders, but by then an unconditional fetch has already gone out for a page this user
     will never see. */
  const {
    events,
    loading: loadingEvents,
    error: eventsError,
  } = useEventList(mayNominate);
  useEventHubLoading(loadingEvents);
  const [contacts, setContacts] = useState<readonly Contact[]>([]);
  const [contactsError, setContactsError] = useState<string | null>(null);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [draft, setDraft] = useState<Draft>(() =>
    editing ? draftFrom(editing) : { ...EMPTY, eventId: lockedEventId ?? '' },
  );
  /* Edit mode: what saving will undo, while the BD is asked to confirm it; null otherwise. */
  const [confirming, setConfirming] = useState<string[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /* Errors show once something has been typed, or once a submit was attempted — not on an
     untouched form, where every required field would start out red. */
  const [triedSubmit, setTriedSubmit] = useState(false);

  /* The BD's BU as the service resolved it (GET /me): undefined until it answers, '' when it
     resolved none. `bdBusinessUnits` is every BU they span; the first is theirs. */
  const [bdBusinessUnit, setBdBusinessUnit] = useState<string | undefined>(undefined);
  const [bdBusinessUnits, setBdBusinessUnits] = useState<readonly string[]>([]);
  /* Every BU name they sit under: an event must be for one of these for them to nominate to it
     (utils/nominatableEvents). Empty until /me answers, and on failure — which matches nothing. */
  const [nominationBus, setNominationBus] = useState<readonly string[]>([]);
  const [profileFailed, setProfileFailed] = useState(false);

  /* Loads the BD's BU, and pre-fills the phone from what they last submitted — only into an
     empty field, so a value typed while this was in flight is kept. A failure leaves the phone
     for the BD to type, but also leaves no BU, which blocks submitting (see below). */
  useEffect(() => {
    /* Not when editing: the phone comes from the nomination, and the BU is the one stamped on
       it — UpdateNomination neither changes it nor checks it. */
    if (!mayNominate || editing) return;
    let cancelled = false;
    eventHubCalendarClient()
      .getCurrentUserProfile()
      .then(({ cellPhone, businessUnit, businessUnits, nominationBusinessUnits }) => {
        if (cancelled) return;
        setBdBusinessUnit(businessUnit ?? '');
        setBdBusinessUnits(businessUnits);
        setNominationBus(nominationBusinessUnits);
        setDraft((d) => ({ ...d, bdCellPhone: d.bdCellPhone || cellPhone || '' }));
      })
      .catch(() => {
        if (!cancelled) setProfileFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [mayNominate, editing]);
  const buLoading = !editing && bdBusinessUnit === undefined && !profileFailed;
  /* Came from an event that is not for any of this BD's BUs: say so and refuse to submit, rather
     than let the service's refusal be the first they hear of it. */
  const isLockedNotForBu = (chosenEvent: Event | undefined) =>
    !editing &&
    !!lockedEventId && !!chosenEvent && !buLoading && !eventIsForBusinessUnits(chosenEvent, nominationBus);
  /* Whether this BD can nominate at all: not while their BU is loading, and never without one. */
  const buBlocked = !editing && !bdBusinessUnit;
  const phoneValid = isValidBdPhone(draft.bdCellPhone);
  const phoneError =
    !phoneValid && (triedSubmit || draft.bdCellPhone.trim() !== '')
      ? draft.bdCellPhone.trim()
        ? 'Enter a mainland China mobile number (11 digits, starting 13–19).'
        : 'BD phone number is required.'
      : undefined;

  const open = useMemo(
    () => (mayNominate ? nominatableEvents(events, new Date(), nominationBus) : []),
    [events, mayNominate, nominationBus],
  );

  /**
   * The event this nomination is for.
   *
   * When locked, looked up across ALL events rather than the open ones: an event whose
   * window has since closed should show as itself and be refused by the service, not
   * silently read as "no event chosen".
   */
  const chosen: Event | undefined = lockedEventId
    ? events.find((e) => e.eventId === lockedEventId)
    : open.find((e) => e.eventId === draft.eventId);
  const lockedNotForBu = isLockedNotForBu(chosen);
  /* Edit mode: why this nomination cannot be edited now, if it cannot (utils/nominationEdit). */
  const blocked = editing ? editBlock(editing, chosen, login, new Date()) : undefined;

  useEffect(() => {
    if (!mayNominate) return;
    const controller = new AbortController();
    let cancelled = false;

    /* Page by page (100 a call): the first page shows at once and the rest keep arriving, so
       the search grows while the BD starts typing — see utils/paging. */
    setContactsLoading(true);
    const client = eventHubCalendarClient();
    loadContactPages(
      (input) => client.listNominationContacts(input),
      (loaded) => {
        if (!cancelled) setContacts(loaded);
      },
      controller.signal,
    )
      .then(() => {
        if (!cancelled) setContactsLoading(false);
      })
      .catch((e: unknown) => {
        if (!cancelled) setContactsLoading(false);
        if (cancelled || controller.signal.aborted) return;
        // Not fatal: a BD can still type the guest in by hand.
        setContactsError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [mayNominate]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  /**
   * The contact box accepts either a pick or free text, so one handler covers both.
   *
   * Matching a known contact copies its details into the fields below — company, job title,
   * phone, email and revenue — and its name into Last / First Name; anything else is taken as the
   * guest's name (in First Name). Which fields may be overwritten is contactAutofill's rule: the ones the BD
   * has not typed into. Losing the match (editing the search text away from the picked name)
   * clears those same untouched fields, so a different person's details are not left behind.
   *
   * The picked contact's Customer Link record (objectId / objectType) is kept and sent with the
   * guest: it is how the service matches this nomination to the campaign member the guest
   * becomes (ListEventAttendees). A typed-in guest has none.
   *
   * The empty value short-circuits. `name` and `email` are both nullable on the wire (the
   * service joins first and last name and yields null when neither is set), so comparing a
   * normalised '' against an empty box would match the first such contact and copy its
   * details over a field the user had just cleared.
   */
  function onContactValue(value: string) {
    const match = value
      ? contacts.find((c) => contactNameOf(c) === value || c.email === value)
      : undefined;
    setDraft((d) => {
      const { fields, filled } = applyAutofill(
        {
          sfdcAccount: d.sfdcAccount,
          companyName: d.companyName,
          jobTitle: d.jobTitle,
          phoneNumber: d.phoneNumber,
          email: d.email,
          companyRevenueSize: d.companyRevenueSize,
          customerBu: d.customerBu,
        },
        d.autofilled,
        contactValues(match),
      );
      return {
        ...d,
        ...fields,
        autofilled: filled,
        ...tagFill(d, match),
        contactSearch: value,
        /* The names follow the box: a picked contact's own parts, else the typed text as a whole
           name in First Name — the way the service itself keeps a name it cannot split. */
        ...(match ? namesOf(match) : { firstName: value, lastName: '' }),
        contactRef: contactRef(match),
      };
    });
  }

  /**
   * The chosen event's questions. Empty for an event that asks nothing extra, and it changes
   * when the picker changes — so the answers below are cleared with it rather than carried
   * over onto a different event's questions at the same indexes.
   */
  const questions = chosen?.nominationForm ?? [];

  useEffect(() => {
    /* Not when editing: the event is fixed, and this would wipe the stored answers the moment
       the event list arrives (chosen goes from undefined to the event). */
    if (editing) return;
    setDraft((d) => (d.formAnswers.length === 0 ? d : { ...d, formAnswers: [] }));
  }, [chosen?.eventId, editing]);

  /* Edit mode: the UpdateNomination request, or null while nothing has changed. */
  const patch = useMemo(
    () => (editing ? buildNominationPatch(editing, formValues(draft, questions.length)) : null),
    [editing, draft, questions.length],
  );

  function setAnswer(index: number, value: string) {
    setDraft((d) => {
      /* Filled to the question count first: a BD who answers only question 3 would otherwise
         produce a sparse array, and `formAnswers` is read by position. */
      const answers = Array.from(
        { length: questions.length },
        (_, i) => d.formAnswers[i] ?? '',
      );
      answers[index] = value;
      return { ...d, formAnswers: answers };
    });
  }

  async function onSubmit() {
    if (!chosen?.eventId) return;
    setTriedSubmit(true);
    if (!phoneValid || buBlocked || lockedNotForBu) return;
    if (editing) {
      if (!patch || blocked) return;
      const lost = editConsequences(editing);
      if (lost.length) {
        setConfirming(lost);
        return;
      }
      await saveEdit();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const input: CreateNominationInput = {
        eventId: chosen.eventId,
        ...formValues(draft, questions.length),
        /* Randomised per submit rather than per render: a retry after a failure is a new
           attempt, while a double-click on one submit is the same one. */
        clientToken: crypto.randomUUID(),
      };
      await eventHubCalendarClient().createNomination(input);
      navigate(`/event/events/${chosen.eventId}`);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  /** Edit mode: sends the patch, then shows the nomination — now back in review — over its list. */
  async function saveEdit() {
    if (!editing?.nominationId || !patch) return;
    setConfirming(null);
    setSubmitting(true);
    setSubmitError(null);
    try {
      await eventHubCalendarClient().updateNomination(patch);
      navigate(nominationHref(editing.nominationId, editing.eventId));
    } catch (e: unknown) {
      setSubmitError(editErrorMessage(e));
      setSubmitting(false);
    }
  }

  /* Contacts with neither a name nor an email are left out: both are nullable on the wire, and
     such a row has nothing a BD could recognise it by. Memoised, since the list can run to
     thousands and the box re-renders on every keystroke. */
  const contactOptions = useMemo(
    () =>
      contacts
        .filter((c) => contactNameOf(c) || c.email)
        .map((c) => ({
          value: (contactNameOf(c) ?? c.email)!,
          description: contactNameOf(c) ? c.email : undefined,
          // The SFDC account's tags and revenue, to tell similar contacts apart.
          tags: contactOptionTags(c),
        })),
    [contacts],
  );

  const eventOptions: SelectProps.Option[] = open.map((e) => ({
    value: e.eventId ?? '',
    label: e.eventName ?? '(untitled)',
    description: e.city,
  }));

  const header = (
    <>
      <BreadcrumbGroup
        items={[
          { text: 'Event Hub', href: '/event' },
          ...(chosen?.eventId
            ? [
                {
                  text: chosen.eventName || 'Event',
                  href: `/event/events/${chosen.eventId}`,
                },
              ]
            : []),
          ...(editing?.nominationId
            ? [
                {
                  text: 'Nomination',
                  href: nominationHref(editing.nominationId, editing.eventId),
                },
              ]
            : []),
          { text: editing ? 'Edit Nomination' : 'Event Nomination', href: '' },
        ]}
        onFollow={(e) => {
          e.preventDefault();
          if (e.detail.href) navigate(e.detail.href);
        }}
      />
      <Header
        variant="h1"
        description={
          editing
            ? 'Only what you change is saved. Any change sends the nomination back for review.'
            : undefined
        }
      >
        {editing ? 'Edit Nomination' : 'Event Nomination'}
      </Header>
    </>
  );

  // POST /nominations is BD/admin only — a marketer is excluded, so collecting a form they
  // cannot submit would waste their time.
  if (!mayNominate) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="You cannot nominate">
          Nominating a guest requires a BD role in Event Hub. Marketers review nominations
          rather than submitting them.
        </Alert>
      </ContentLayout>
    );
  }

  if (eventsError) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="Failed to load events">
          {eventsError}
        </Alert>
      </ContentLayout>
    );
  }

  /* Until /me answers there are no BUs to match, so every event would read as closed; wait rather
     than flash "nothing is open". */
  if (loadingEvents || (mayNominate && buLoading)) {
    // The layout's overlay shows meanwhile.
    return <ContentLayout header={header}>{null}</ContentLayout>;
  }

  // Arrived with an id that is not an event this caller can see at all.
  if (lockedEventId && !chosen) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="Event not found">
          No event matches <Box variant="code">{lockedEventId}</Box>.
        </Alert>
      </ContentLayout>
    );
  }

  // Edit mode: not this reader's nomination, or its event has stopped taking nominations.
  if (editing && blocked) {
    return (
      <ContentLayout header={header}>
        <Alert type="info" header="This nomination can't be edited">
          {blocked === 'not-creator'
            ? 'Only the BD who submitted a nomination can edit it.'
            : "Nominations can be edited only while their event's nomination window is open, and this event's is not."}
        </Alert>
      </ContentLayout>
    );
  }

  // Nothing is open, so there is nothing to nominate to — distinct from a missing event.
  if (!lockedEventId && open.length === 0) {
    return (
      <ContentLayout header={header}>
        <Alert type="info" header="No events are open for nomination">
          Nominations are accepted only while an event's nomination window is open, and only
          to events for your BU. The events open to you appear on the Event Hub home page.
        </Alert>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout header={header}>
      <form onSubmit={(e) => e.preventDefault()}>
        <Form
          errorText={submitError ?? undefined}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() =>
                  navigate(
                    editing?.nominationId
                      ? nominationHref(editing.nominationId, editing.eventId)
                      : '/event',
                  )
                }
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={submitting}
                disabled={!chosen || buBlocked || lockedNotForBu || (!!editing && !patch)}
                onClick={onSubmit}
              >
                {editing ? 'Save Changes' : 'Submit Nomination'}
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            {lockedNotForBu ? (
              <Alert type="warning" header="This event is not for your BU">
                {chosen?.businessUnits?.length
                  ? `It is for ${chosen.businessUnits.join(', ')}; `
                  : 'It has no BU set yet; '}
                you can nominate only to events for your BU
                {nominationBus.length ? ` (${nominationBus.join(', ')})` : ''}.
              </Alert>
            ) : null}
            <Container header={<Header variant="h2">Nomination Details</Header>}>
              <SpaceBetween size="l">
                <FormField
                  label="Select Event"
                  description={
                    editing
                      ? "A nomination's event cannot be changed."
                      : lockedEventId
                      ? 'Set by the event you came from.'
                      : 'Only events currently open for nomination.'
                  }
                >
                  {lockedEventId ? (
                    /* Read-only text, not a disabled Select: the choice was already made
                       and a greyed-out dropdown invites trying to change it. */
                    <span className="eh-nomination__locked-event">
                      <Box variant="strong">{chosen?.eventName || lockedEventId}</Box>
                    </span>
                  ) : (
                    <Select
                      selectedOption={
                        eventOptions.find((o) => o.value === draft.eventId) ?? null
                      }
                      placeholder="Choose an event"
                      options={eventOptions}
                      onChange={({ detail }) =>
                        set('eventId', String(detail.selectedOption.value))
                      }
                    />
                  )}
                </FormField>

                <Box variant="h5">Contact Information</Box>

                {contactsError ? (
                  <Alert type="warning" header="Could not load your contacts">
                    {contactsError} — enter the guest's details by hand instead.
                  </Alert>
                ) : null}

                <FormField
                  label="Select or Create New Contact"
                  description="Search your contacts to fill in the details below. For someone not in the list, type their name and choose “Use …” — no need to wait for the list to finish loading."
                  constraintText={
                    <>
                      Contacts are resolved from the territory IDs you have management permission
                      for in the <SalesHierarchyLink />.
                    </>
                  }
                >
                  {/* Autosuggest, not Select: the field takes either. The list is this BD's
                      lead contacts, resolved from their sales territories, and it can be
                      legitimately empty — a caller who manages no territory gets none. Typing
                      a name that is not in it is expected, so the control must not force a
                      choice from the list. */}
                  <Autosuggest
                    value={draft.contactSearch}
                    onChange={({ detail }) => onContactValue(detail.value)}
                    options={contactOptions}
                    /* A BD can have thousands of contacts: render only the visible options,
                       so typing stays responsive while later pages keep arriving. */
                    virtualScroll
                    placeholder="Search a contact, or type a new guest's name"
                    enteredTextLabel={(value) => `Use "${value}" as a new contact`}
                    empty="No contacts in your territories"
                    /* Shown at the end of the list while later pages are still arriving, so
                       a contact not found yet is not taken as one the BD does not have — and
                       says the box need not wait for it (testing doc #36). */
                    statusType={contactsLoading ? 'loading' : 'finished'}
                    loadingText={`Still loading your contacts (${contacts.length} so far). You can keep typing, or use a new name now.`}
                    ariaLabel="Select or Create New Contact"
                  />
                </FormField>

                <ColumnLayout columns={2}>
                  {/* Pre-filled from the picked contact's SFDC account tags; the BD may change
                      them. Tags the list does not have are kept and offered (tagOptions). */}
                  <FormField label="Account Tags">
                    <Multiselect
                      selectedOptions={draft.accountTags.map((t) => ({ value: t, label: t }))}
                      options={tagOptions(draft.accountTags).map((t) => ({ value: t, label: t }))}
                      placeholder="Choose tags"
                      onChange={({ detail }) =>
                        set(
                          'accountTags',
                          detail.selectedOptions.map((o) => String(o.value)),
                        )
                      }
                    />
                  </FormField>
                  {/* The SFDC account id, submitted as accountInformation.sfdcAccountId. Filled
                      from the picked contact's SFDC account. */}
                  <FormField label="SFDC Account">
                    <Input
                      value={draft.sfdcAccount}
                      onChange={({ detail }) => set('sfdcAccount', detail.value)}
                    />
                  </FormField>

                  <FormField label="Company Name">
                    <Input
                      value={draft.companyName}
                      onChange={({ detail }) => set('companyName', detail.value)}
                    />
                  </FormField>
                  <UnsavedField
                    label="Company Revenue Size"
                    value={draft.companyRevenueSize}
                    onChange={(v) => set('companyRevenueSize', v)}
                  />

                  {/* Family name first, as a Chinese name is written; the service stores the two
                      apart. A picked contact fills both. */}
                  <FormField label="Last Name (姓)">
                    <Input
                      value={draft.lastName}
                      onChange={({ detail }) => set('lastName', detail.value)}
                    />
                  </FormField>
                  <FormField label="First Name (名)">
                    <Input
                      value={draft.firstName}
                      onChange={({ detail }) => set('firstName', detail.value)}
                    />
                  </FormField>
                  {/* The UserCenter / mini-app title list: shows zh-CN, stores the English key.
                      An autofilled title the list lacks is shown as given — see jobTitles. */}
                  <FormField label="Job Title">
                    <Select
                      selectedOption={
                        draft.jobTitle
                          ? { value: draft.jobTitle, label: jobTitleLabel(draft.jobTitle) }
                          : null
                      }
                      placeholder="Choose a job title"
                      options={[...JOB_TITLE_OPTIONS]}
                      filteringType="auto"
                      onChange={({ detail }) =>
                        set('jobTitle', String(detail.selectedOption.value))
                      }
                    />
                  </FormField>

                  <FormField label="Phone Number">
                    <Input
                      value={draft.phoneNumber}
                      onChange={({ detail }) => set('phoneNumber', detail.value)}
                    />
                  </FormField>
                  <FormField label="Email">
                    <Input
                      type="email"
                      value={draft.email}
                      onChange={({ detail }) => set('email', detail.value)}
                    />
                  </FormField>
                  {/* The customer's BU — not the BD's own (BD Information, below). Filled from the
                      picked contact's SFDC account; the BU list is offered, and anything else is
                      kept as typed, since SFDC's names need not match it. */}
                  <FormField label="Customer BU">
                    <Autosuggest
                      value={draft.customerBu}
                      onChange={({ detail }) => set('customerBu', detail.value)}
                      options={BU_OPTIONS}
                      placeholder="Choose or enter the customer's BU"
                      enteredTextLabel={(value) => `Use "${value}"`}
                      ariaLabel="Customer BU"
                    />
                  </FormField>
                </ColumnLayout>
              </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">BD Information</Header>}>
              <ColumnLayout columns={2}>
                <FormField
                  label="BD Phone Number"
                  errorText={phoneError}
                  constraintText="Mainland China mobile, e.g. 138 0013 8000"
                >
                  <Input
                    inputMode="tel"
                    value={draft.bdCellPhone}
                    onChange={({ detail }) => set('bdCellPhone', detail.value)}
                  />
                </FormField>
                <FormField
                  label="BD BU"
                  constraintText={
                    <>
                      {/* Several BUs: product chose "the first", for simplicity, over a picker —
                          say so, so a leader spanning BUs knows where their nominations count. */}
                      {editing
                        ? 'The BU recorded when this nomination was submitted. '
                        : ''}
                      {!editing && bdBusinessUnit && bdBusinessUnits.length > 1
                        ? `Your territories span ${bdBusinessUnits.length} BUs ` +
                          `(${bdBusinessUnits.join(', ')}). For simplicity, your nominations ` +
                          `count toward the first, ${bdBusinessUnit}. `
                        : ''}
                      Your BU is managed in the <SalesHierarchyLink /> system.
                    </>
                  }
                >
                  {/* Read-only, never typed into: the service decides the BU. */}
                  <Input
                    readOnly
                    value={(editing ? editing.bdBusinessUnit : bdBusinessUnit) ?? ''}
                    placeholder={buLoading ? 'Loading your BU…' : undefined}
                  />
                </FormField>
              </ColumnLayout>
              {!buLoading && buBlocked ? (
                <Box margin={{ top: 'l' }}>
                  <Alert type="error" header="You can't nominate yet">
                    {profileFailed
                      ? "Your BU couldn't be loaded. Reload the page to try again."
                      : 'Your BU comes from your territory in the GCR sales hierarchy, and none was ' +
                        'found for you. Ask your manager to check your territory assignment; if it ' +
                        'changed recently, try again later.'}
                  </Alert>
                </Box>
              ) : null}
            </Container>

            {/* Only when there is something to ask. An event whose marketer configured no
                questions gets no panel at all — an empty one reads as a section that failed
                to load rather than as one that does not apply. */}
            {questions.length > 0 ? (
              <Container
                header={<Header variant="h2">Travel &amp; Event Arrangements</Header>}
              >
                <SpaceBetween size="l">
                  {questions.map((question, index) => (
                    /* Keyed by index, not by the question text: the wording is not unique
                       (nothing stops a marketer asking the same thing twice) and position is
                       what identifies an answer anyway. */
                    <FormField
                      key={index}
                      // Numbered as well as worded, so two similar questions stay distinct
                      // and an unanswered one can be named in a message.
                      label={`${index + 1}. ${question}`}
                    >
                      <Input
                        value={draft.formAnswers[index] ?? ''}
                        onChange={({ detail }) => setAnswer(index, detail.value)}
                      />
                    </FormField>
                  ))}
                </SpaceBetween>
              </Container>
            ) : null}
          </SpaceBetween>
        </Form>
      </form>
      <Modal
        visible={confirming !== null}
        onDismiss={() => setConfirming(null)}
        header="Send this nomination back for review?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirming(null)}>
                Keep editing
              </Button>
              <Button variant="primary" onClick={saveEdit}>
                Save and resubmit
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          {(confirming ?? []).map((line) => (
            <Box key={line}>{line}</Box>
          ))}
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
}
